/**
 * Écran détail client admin /admin/clients/:id.
 * Profil + édition (firstName, lastName, phone, clientType) + liste des
 * projets du client avec lien vers leur fiche admin.
 */
import {
  updateClientSchema,
  type Project,
  type PublicUser,
  type UpdateClientInput,
} from '@bcl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { getClientDetail, updateClient } from '@/features/admin/admin-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { getStatusColor } from '@/features/dashboard/status-color';
import {
  requestTypeLabels,
  statusLabels,
} from '@/features/projects/labels';

const CLIENT_TYPES = [
  'particulier',
  'entreprise',
  'promoteur',
  'amenageur',
  'copropriete',
] as const;

export function AdminClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const clientId = id ?? '';
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const detail = useQuery({
    queryKey: ['admin', 'client', clientId],
    queryFn: () => getClientDetail(clientId),
    enabled: !!clientId,
  });

  return (
    <DashboardLayout variant="admin" title="Détail client">
      <div className="space-y-6">
        <div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/clients">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour aux clients
            </Link>
          </Button>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle>
                {detail.data
                  ? `${detail.data.client.firstName} ${detail.data.client.lastName}`
                  : 'Chargement…'}
              </CardTitle>
              <CardDescription>Profil client.</CardDescription>
            </div>
            {detail.data && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Éditer
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!detail.data ? (
              <p className="text-sm text-gray-500">Chargement…</p>
            ) : (
              <ProfileGrid client={detail.data.client} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dossiers du client</CardTitle>
            <CardDescription>
              {detail.data
                ? `${detail.data.projects.length} dossier(s).`
                : 'Chargement…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ProjectsTable projects={detail.data?.projects ?? []} />
          </CardContent>
        </Card>

        {editing && detail.data && (
          <EditClientDialog
            client={detail.data.client}
            onClose={() => setEditing(false)}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['admin', 'client', clientId] });
              qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
              setEditing(false);
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function ProfileGrid({ client }: { client: PublicUser }) {
  return (
    <dl className="grid gap-3 text-sm sm:grid-cols-2">
      <Field label="Email">{client.email}</Field>
      <Field label="Téléphone">{client.phone}</Field>
      <Field label="Type">{client.clientType ?? '—'}</Field>
      <Field label="Email vérifié">
        <Badge
          variant="outline"
          className={
            client.emailVerified
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }
        >
          {client.emailVerified ? 'Oui' : 'Non'}
        </Badge>
      </Field>
      <Field label="Inscrit le">
        {new Date(client.createdAt).toLocaleDateString('fr-FR')}
      </Field>
    </dl>
  );
}

function ProjectsTable({ projects }: { projects: Project[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Référence</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Statut</TableHead>
          <TableHead>Créé le</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {projects.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center text-sm text-gray-500">
              Aucun dossier.
            </TableCell>
          </TableRow>
        ) : (
          projects.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.reference}</TableCell>
              <TableCell>{requestTypeLabels[p.requestType]}</TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={getStatusColor(statusLabels[p.status])}
                >
                  {statusLabels[p.status]}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-gray-500">
                {new Date(p.createdAt).toLocaleDateString('fr-FR')}
              </TableCell>
              <TableCell className="text-right">
                <Button asChild size="sm" variant="outline">
                  <Link to={`/admin/projects/${p.id}`}>Voir détails</Link>
                </Button>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function EditClientDialog({
  client,
  onClose,
  onSaved,
}: {
  client: PublicUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const form = useForm<UpdateClientInput>({
    resolver: zodResolver(updateClientSchema),
    defaultValues: {
      firstName: client.firstName,
      lastName: client.lastName,
      phone: client.phone,
      clientType: client.clientType ?? undefined,
    },
  });

  useEffect(() => {
    form.reset({
      firstName: client.firstName,
      lastName: client.lastName,
      phone: client.phone,
      clientType: client.clientType ?? undefined,
    });
  }, [client, form]);

  const mut = useMutation({
    mutationFn: (input: UpdateClientInput) => updateClient(client.id, input),
    onSuccess: () => {
      toast.success('Client mis à jour');
      onSaved();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Éditer le client</DialogTitle>
          <DialogDescription>{client.email}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((values) => mut.mutate(values))}
          className="space-y-4"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="client-first-name">Prénom</Label>
              <Input id="client-first-name" {...form.register('firstName')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="client-last-name">Nom</Label>
              <Input id="client-last-name" {...form.register('lastName')} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="client-phone">Téléphone</Label>
            <Input id="client-phone" {...form.register('phone')} />
            {form.formState.errors.phone && (
              <p className="text-xs text-destructive">
                {form.formState.errors.phone.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="client-type">Type de client</Label>
            <select
              id="client-type"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              {...form.register('clientType')}
            >
              {CLIENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{children}</dd>
    </div>
  );
}
